import React, { useEffect, useMemo, useState } from "react";
import { Receipt, Search, Download, Mail, AlertCircle, Paperclip, Trash2, FileDown, X, Plus, FlaskConical, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatDeShort } from "@/lib/date";
import InvoiceDeliveryBadge from "@/components/InvoiceDeliveryBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

const fmtEur = (v) => `${Number(v || 0).toFixed(2).replace(".", ",")} €`;
const fmtUe = (v) => `${Number(v || 0).toLocaleString("de-DE", { maximumFractionDigits: 1 })}`;
const MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1] || m} ${y}`;
};
const REASON_LABEL = { ue_erschoepft: "UE verbraucht", massnahme_beendet: "Maßnahme beendet", manuell_markiert: "Jetzt abrechnen (manuell)", rest_offen: "Rest-Rechnung (neues Modul offen)" };

export default function BillingCenterTab() {
  const [worklist, setWorklist] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [coachFilter, setCoachFilter] = useState("");
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);
  const [activeAttachmentIdx, setActiveAttachmentIdx] = useState(0);
  const [previewing, setPreviewing] = useState(null); // client_id currently loading preview
  const [sending, setSending] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [clientPreview, setClientPreview] = useState(null);
  const [loadingClientPreview, setLoadingClientPreview] = useState(null); // invoice_id currently loading
  const [clientAttachmentIdx, setClientAttachmentIdx] = useState(0);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [correction, setCorrection] = useState(null); // { invoice, rows }
  const [correctionLoading, setCorrectionLoading] = useState(null); // invoice_id currently loading
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);

  const [delivery, setDelivery] = useState({});
  const [deliveryNote, setDeliveryNote] = useState(null);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const loadDelivery = (force = false) => {
    setCheckingDelivery(true);
    api.get("/invoices/delivery-status", { params: { force }, timeout: 120000 })
      .then((r) => { setDelivery(r.data.invoices); setDeliveryNote(r.data.api_error); if (force && !r.data.api_error) toast.success("Zustellstatus aktualisiert"); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => setCheckingDelivery(false));
  };

  const loadAll = () => {
    loadDelivery();
    api.get("/invoices/billing-worklist").then((r) => setWorklist(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get("/invoices").then((r) => setInvoices(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get("/invoices/forecast").then((r) => setForecast(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  useEffect(() => { loadAll(); }, []);

  const coachOptions = useMemo(() => {
    const set = new Set();
    (worklist || []).forEach((r) => {
      (r.coach_names || []).forEach((n) => n && set.add(n));
      if (r.primary_coach_name) set.add(r.primary_coach_name);
    });
    return Array.from(set).sort();
  }, [worklist]);

  const filteredWorklist = useMemo(() => {
    if (!worklist) return [];
    if (!coachFilter) return worklist;
    return worklist.filter((r) => (r.coach_names || []).includes(coachFilter) || r.primary_coach_name === coachFilter);
  }, [worklist, coachFilter]);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];
    const q = query.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((inv) =>
      (inv.client_name || "").toLowerCase().includes(q) ||
      (inv.invoice_number || "").toLowerCase().includes(q) ||
      (inv.kostentraeger || "").toLowerCase().includes(q) ||
      (inv.measure_name || "").toLowerCase().includes(q)
    );
  }, [invoices, query]);

  const totalAmount = useMemo(() => filteredInvoices.reduce((s, inv) => s + Number(inv.total_amount || 0), 0), [filteredInvoices]);

  const openPreview = async (clientId, remainingOnly = false) => {
    setPreviewing(clientId);
    try {
      const { data } = await api.post(`/clients/${clientId}/invoice/preview`, {}, { params: { remaining_only: remainingOnly }, timeout: 90000 });
      setPreview({ ...data, client_id: clientId, remaining_only: remainingOnly });
      setActiveAttachmentIdx(0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setPreviewing(null); }
  };

  const confirmSend = async () => {
    if (!preview) return;
    setSending(true);
    try {
      const { data } = await api.post(`/clients/${preview.client_id}/invoice/generate-and-send`, {}, { params: { remaining_only: preview.remaining_only }, timeout: 60000 });
      if (data.delivery_failed) toast.error(`Rechnung ${data.invoice_number} erstellt, aber die E-Mail konnte NICHT zugestellt werden – bitte Adresse prüfen und „Nachsenden“ nutzen`);
      else toast.success(`Rechnung ${data.invoice_number} erstellt und an den Leistungsträger versendet`);
      setPreview(null);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSending(false); }
  };

  const sendTestEmail = async () => {
    if (!preview) return;
    setSendingTest(true);
    try {
      const { data } = await api.post(`/clients/${preview.client_id}/invoice/send-test-email`, {}, { params: { remaining_only: preview.remaining_only }, timeout: 60000 });
      const ccNote = data.cc && data.cc.length > 0 ? ` (CC: ${data.cc.join(", ")})` : "";
      toast.success(`Test-E-Mail an ${data.sent_to} gesendet${ccNote}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSendingTest(false); }
  };

  const [resendingCc, setResendingCc] = useState(null);
  const resendCc = async (invoiceId) => {
    setResendingCc(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/resend-cc`, {}, { timeout: 60000 });
      toast.success(`Erneut gesendet an ${data.sent.join(", ")}`);
      if (data.failed.length) toast.error(`Nicht zugestellt an ${data.failed.join(", ")}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setResendingCc(null); }
  };

  const resendLt = async (invoiceId) => {
    setResendingCc(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/resend-leistungstraeger`, {}, { timeout: 60000 });
      toast.success(`Nachgesendet an ${data.sent_to}${data.cc.length ? ` (CC: ${data.cc.join(", ")})` : ""}`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setResendingCc(null); }
  };

  const openClientPreview = async (invoiceId) => {
    setLoadingClientPreview(invoiceId);
    try {
      const { data } = await api.post(`/invoices/${invoiceId}/send-to-client/preview`, {}, { timeout: 60000 });
      setClientPreview(data);
      setClientAttachmentIdx(0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoadingClientPreview(null); }
  };

  const confirmSendToClient = async () => {
    if (!clientPreview) return;
    setSendingToClient(true);
    try {
      const { data } = await api.post(`/invoices/${clientPreview.invoice_id}/send-to-client`, {}, { timeout: 60000 });
      toast.success(`Unterlagen an ${data.sent_to_client_email} gesendet`);
      setClientPreview(null);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSendingToClient(false); }
  };

  const downloadInvoice = async (invoiceId, invoiceNumber, inv) => {
    try {
      const url = inv?.type === "wb" ? `/billing/wb-installments/${inv.participant_id}/${inv.nr}/pdf` : `/invoices/${invoiceId}/pdf`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const u = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = u; link.download = `Rechnung_${invoiceNumber}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(u);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const [deletingInvoiceId, setDeletingInvoiceId] = useState(null);
  const deleteInvoice = async (invoiceId) => {
    setDeletingInvoiceId(invoiceId);
    try {
      const { data } = await api.delete(`/invoices/${invoiceId}`);
      toast.success(`Rechnung ${data.deleted_invoice_number} gelöscht`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDeletingInvoiceId(null); }
  };

  const [exportingCsv, setExportingCsv] = useState(false);
  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const res = await api.get("/invoices/export-csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const u = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = u; link.download = `Rechnungen_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(u);
      toast.success("CSV-Export heruntergeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setExportingCsv(false); }
  };

  const resetAllInvoices = async () => {
    try {
      const { data } = await api.post("/clients/invoice/reset-all", {});
      toast.success(`${data.deleted_count} Rechnung(en) gelöscht — nächste Rechnung wird wieder ${data.next_invoice_number} sein`);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadPdfBase64 = (pdfBase64, filename) => {
    const bytes = atob(pdfBase64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: "application/pdf" });
    const u = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = u; link.download = filename;
    document.body.appendChild(link); link.click(); link.remove();
    window.URL.revokeObjectURL(u);
  };

  const openCorrection = async (inv) => {
    setCorrectionLoading(inv.invoice_id);
    try {
      const { data } = await api.get(`/clients/${inv.client_id}/invoices/${inv.invoice_id}/correction-base`);
      setCorrection({ invoice: inv, rows: data.rows.map((r) => ({ ...r, ue: r.ue })) });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCorrectionLoading(null); }
  };

  const correctionTotal = useMemo(() => {
    if (!correction) return { ue: 0, amount: 0 };
    return correction.rows.reduce((acc, r) => ({
      ue: acc.ue + Number(r.ue || 0),
      amount: acc.amount + Number(r.ue || 0) * Number(r.per_ue || 0),
    }), { ue: 0, amount: 0 });
  }, [correction]);

  const updateCorrectionRow = (idx, ue) => {
    setCorrection((prev) => ({
      ...prev,
      rows: prev.rows.map((r, i) => (i === idx ? { ...r, ue } : r)),
    }));
  };

  const submitCorrection = async () => {
    if (!correction) return;
    setCorrectionSubmitting(true);
    try {
      const { data } = await api.post(
        `/clients/${correction.invoice.client_id}/invoices/${correction.invoice.invoice_id}/correction`,
        { rows: correction.rows.map((r) => ({ name: r.name, ue: Number(r.ue || 0) })) },
      );
      toast.success(`Korrekturrechnung ${data.invoice_number} erstellt (Korrektur zu ${correction.invoice.invoice_number})`);
      downloadPdfBase64(data.pdf_base64, `Korrekturrechnung_${data.invoice_number}.pdf`);
      setCorrection(null);
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setCorrectionSubmitting(false); }
  };

  return (
    <div data-testid="billing-center-tab">
      <CcRecipientsCard />

      {/* --- 1. Worklist: Kunden, die abgerechnet werden sollen --- */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">
            Kunden, die abgerechnet werden sollen {worklist ? `(${filteredWorklist.length})` : ""}
          </div>
          {coachOptions.length > 0 && (
            <select data-testid="worklist-coach-filter" className="input-tech text-xs w-auto" value={coachFilter} onChange={(e) => setCoachFilter(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {coachOptions.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          )}
        </div>
        <div className="border border-slate-200 bg-white" data-testid="worklist-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-medium">Kunde</th>
                  <th className="text-left p-3 font-medium">Coach</th>
                  <th className="text-left p-3 font-medium">Ende</th>
                  <th className="text-left p-3 font-medium">UE</th>
                  <th className="text-left p-3 font-medium">Grund</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {!worklist ? (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Lädt…</td></tr>
                ) : filteredWorklist.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-400 italic">Aktuell keine offenen Rechnungen.</td></tr>
                ) : filteredWorklist.map((r) => (
                  <tr key={r.client_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`worklist-row-${r.client_id}`}>
                    <td className="p-3">
                      <Link to={`/avgs/${r.client_id}`} className="text-slate-900 font-medium hover:underline">{r.client_name}</Link>
                    </td>
                    <td className="p-3 text-slate-600">{r.primary_coach_name || (r.coach_names || []).join(", ") || "—"}</td>
                    <td className="p-3 font-mono text-slate-600">{r.end_date ? formatDeShort(r.end_date) : "—"}</td>
                    <td className="p-3 font-mono text-slate-700">{fmtUe(r.used_ue)} / {fmtUe(r.total_ue)}</td>
                    <td className="p-3">
                      {r.reasons.map((reason) => (
                        <span key={reason}
                          className={`inline-block mr-1 mb-1 px-2 py-0.5 border text-[10px] ${reason === "rest_offen" ? "bg-sky-50 border-sky-300 text-sky-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                          {REASON_LABEL[reason] || reason}
                        </span>
                      ))}
                      {!r.arbeitsvermittler_email && (
                        <div className="flex items-center gap-1 text-[10px] text-red-600 mt-1">
                          <AlertCircle size={10} strokeWidth={1.5} /> Keine Arbeitsvermittler-E-Mail
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => openPreview(r.client_id, r.is_partial)} disabled={previewing === r.client_id}
                        data-testid={`worklist-generate-${r.client_id}`}
                        className="bg-slate-900 text-white px-3 py-1.5 text-[11px] font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center gap-1 ml-auto">
                        <Receipt size={11} strokeWidth={1.5} /> {previewing === r.client_id ? "Lädt…" : r.is_partial ? "Rest-Rechnung erstellen" : "Rechnung erstellen"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- 2. Zahlungs-Prognose --- */}
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-3">
          Zahlungs-Prognose <span className="text-slate-400 normal-case font-normal">— Annahme: Zahlungseingang 4 Wochen nach Rechnungsdatum</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-200 bg-white" data-testid="forecast-by-invoice-month">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
              Abgerechnet nach Rechnungsdatum
            </div>
            {!forecast || forecast.by_invoice_month.length === 0 ? (
              <div className="p-4 text-xs text-slate-400 italic">Noch keine Daten.</div>
            ) : forecast.by_invoice_month.map((m) => (
              <div key={m.month} className="flex items-center justify-between px-4 py-2 border-b border-slate-100 last:border-0 text-xs">
                <span className="font-mono text-slate-700">{fmtMonth(m.month)}</span>
                <span className="text-slate-500">{fmtUe(m.ue)} UE · {m.count} Rg.</span>
                <span className="font-mono font-medium text-slate-900">{fmtEur(m.amount)}</span>
              </div>
            ))}
          </div>
          <div className="border border-slate-200 bg-white" data-testid="forecast-by-payment-month">
            <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
              Erwarteter Zahlungseingang
            </div>
            {!forecast || forecast.by_payment_month.length === 0 ? (
              <div className="p-4 text-xs text-slate-400 italic">Noch keine Daten.</div>
            ) : forecast.by_payment_month.map((m) => (
              <div key={m.month} className="flex items-center justify-between px-4 py-2 border-b border-slate-100 last:border-0 text-xs">
                <span className="font-mono text-slate-700">{fmtMonth(m.month)}</span>
                <span className="text-slate-500">{fmtUe(m.ue)} UE · {m.count} Rg.</span>
                <span className="font-mono font-medium text-slate-900">{fmtEur(m.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* --- 3. Bisherige Rechnungen --- */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Receipt size={14} strokeWidth={1.5} className="text-slate-500" />
            <p className="text-xs text-slate-500">Bisherige Rechnungen (AVGS + WB-Abschläge, ein Nummernkreis), über alle Kunden hinweg.</p>
          </div>
          {deliveryNote && <div className="w-full text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="invoices-delivery-note">{deliveryNote}</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => loadDelivery(true)} disabled={checkingDelivery} data-testid="invoices-delivery-refresh-btn"
              className="text-[11px] font-bold uppercase tracking-overline text-slate-700 hover:text-slate-900 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
              <Mail size={12} strokeWidth={1.5} /> {checkingDelivery ? "Prüfe…" : "Status aktualisieren"}
            </button>
            <button onClick={exportCsv} disabled={exportingCsv} data-testid="invoices-export-csv-btn"
              className="text-[11px] font-bold uppercase tracking-overline text-slate-700 hover:text-slate-900 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5">
              <FileDown size={12} strokeWidth={1.5} /> {exportingCsv ? "Exportiere…" : "CSV exportieren"}
            </button>
            <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="invoices-reset-all-btn" className="text-[11px] font-bold uppercase tracking-overline text-red-600 hover:text-red-800 border border-red-200 px-3 py-1.5 hover:bg-red-50">
                Alle Rechnungen zurücksetzen
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="invoices-reset-all-confirm">
              <AlertDialogHeader>
                <AlertDialogTitle>Wirklich ALLE Rechnungen löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Löscht unwiderruflich alle bisher erstellten Rechnungen (inkl. Historie) und setzt die
                  Rechnungsnummer zurück (nach der höchsten vergebenen WB-Abschlagsnummer, sonst RE000115). WB-Abschlagsrechnungen bleiben erhalten. Bereits versendete E-Mails werden dadurch
                  NICHT zurückgezogen.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction data-testid="invoices-reset-all-confirm-btn" onClick={resetAllInvoices} className="bg-red-600 hover:bg-red-700">
                  Ja, alle löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
          <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-invoices-stat-count">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Rechnungen</div>
            <div className="font-heading text-3xl font-light mt-1 text-slate-900">{filteredInvoices.length}</div>
          </div>
          <div className="border border-slate-200 bg-white p-4" data-testid="mgmt-invoices-stat-sum">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Summe</div>
            <div className="font-heading text-3xl font-light mt-1 text-slate-900">{fmtEur(totalAmount)}</div>
          </div>
        </div>
        <div className="relative mb-4 max-w-sm">
          <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="mgmt-invoices-search"
            className="input-tech pl-8 font-mono text-sm"
            placeholder="Suche: Kunde, Rechnungs-Nr., Leistungsträger, Maßnahme…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="border border-slate-200 bg-white" data-testid="mgmt-invoices-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left p-3 font-medium">Datum</th>
                  <th className="text-left p-3 font-medium">Rechnungs-Nr.</th>
                  <th className="text-left p-3 font-medium">Kunde</th>
                  <th className="text-left p-3 font-medium">Leistungsträger</th>
                  <th className="text-left p-3 font-medium">Maßnahme</th>
                  <th className="text-right p-3 font-medium">UE</th>
                  <th className="text-right p-3 font-medium">Betrag</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {!invoices ? (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400 italic">Lädt…</td></tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400 italic">Keine Rechnungen gefunden.</td></tr>
                ) : filteredInvoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mgmt-invoice-row-${inv.invoice_id}`}>
                    <td className="p-3 font-mono text-slate-600">{formatDeShort(inv.invoice_date)}</td>
                    <td className="p-3 font-mono font-medium text-slate-900">
                      {inv.invoice_number}
                      <div className={`text-[10px] font-sans font-normal inline-block px-1.5 py-0.5 mt-1 ml-1 border ${inv.type === "wb" ? "text-[#0a788c] bg-[#0a788c]/10 border-[#0a788c]/30" : "text-slate-600 bg-slate-50 border-slate-200"}`} data-testid={`mgmt-invoice-type-${inv.invoice_id}`}>
                        {inv.type === "wb" ? "WB-Abschlag" : "AVGS"}
                      </div>
                      {inv.is_correction && (
                        <div className="text-[10px] font-sans font-normal text-amber-700 bg-amber-50 border border-amber-200 inline-block px-1.5 py-0.5 mt-1" data-testid={`mgmt-invoice-correction-tag-${inv.invoice_id}`}>
                          Korrektur zu {inv.correction_of_invoice_number}
                        </div>
                      )}
                      {inv.sent_to_client_at && (
                        <div className="text-[10px] font-sans font-normal text-emerald-700 bg-emerald-50 border border-emerald-200 inline-block px-1.5 py-0.5 mt-1" data-testid={`mgmt-invoice-sent-to-client-tag-${inv.invoice_id}`}>
                          ✓ An Kunden gesendet
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <Link to={inv.type === "wb" ? `/weiterbildung/kunden/${inv.participant_id}?tab=unterlagen` : `/avgs/${inv.client_id}`} className="text-slate-900 font-medium hover:underline" data-testid={`mgmt-invoice-client-link-${inv.invoice_id}`}>
                        {inv.client_name}
                      </Link>
                    </td>
                    <td className="p-3 text-slate-700">
                      {inv.kostentraeger}
                      <div className="text-[10px] text-slate-400 font-mono">{inv.kostentraeger_email}</div>
                    </td>
                    <td className="p-3 text-slate-600">{inv.measure_name}</td>
                    <td className="p-3 text-right font-mono text-slate-700">{inv.total_ue != null ? fmtUe(inv.total_ue) : "—"}</td>
                    <td className="p-3 text-right font-mono font-medium text-slate-900">{fmtEur(inv.total_amount)}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <InvoiceDeliveryBadge invoiceId={inv.invoice_id} info={delivery[inv.invoice_id]} />
                        {inv.type === "wb" && (
                          <span className={`text-[10px] font-bold uppercase tracking-overline ${inv.datev_status === "sent" ? "text-emerald-700" : "text-red-600"}`} data-testid={`mgmt-invoice-datev-${inv.invoice_id}`}>
                            {inv.datev_status === "sent" ? "an DATEV" : "DATEV fehlgeschlagen"}
                          </span>
                        )}
                        {!inv.is_correction && inv.type !== "wb" && (
                          <button onClick={() => openCorrection(inv)} disabled={correctionLoading === inv.invoice_id}
                            data-testid={`mgmt-invoice-correction-btn-${inv.invoice_id}`}
                            className="text-[10px] font-bold uppercase tracking-overline text-amber-700 hover:text-amber-900 disabled:opacity-50">
                            {correctionLoading === inv.invoice_id ? "Lädt…" : "Korrektur"}
                          </button>
                        )}
                        <button onClick={() => downloadInvoice(inv.invoice_id, inv.invoice_number, inv)} data-testid={`mgmt-invoice-download-${inv.invoice_id}`}
                          className="text-[10px] font-bold uppercase tracking-overline text-slate-600 hover:text-slate-900 flex items-center gap-1">
                          <Download size={11} strokeWidth={1.5} /> PDF
                        </button>
                        {inv.type !== "wb" && <>
                        <button onClick={() => openClientPreview(inv.invoice_id)} disabled={loadingClientPreview === inv.invoice_id}
                          data-testid={`mgmt-invoice-send-to-client-${inv.invoice_id}`}
                          className="text-[10px] font-bold uppercase tracking-overline text-sky-700 hover:text-sky-900 flex items-center gap-1 disabled:opacity-50">
                          <Send size={11} strokeWidth={1.5} /> {loadingClientPreview === inv.invoice_id ? "Lädt…" : "An Kunden senden"}
                        </button>
                        {inv.delivery_failed && (
                          <button onClick={() => resendLt(inv.invoice_id)} disabled={resendingCc === inv.invoice_id}
                            data-testid={`mgmt-invoice-resend-lt-${inv.invoice_id}`} title="Die E-Mail an den Leistungsträger wurde nicht zugestellt"
                            className="text-[10px] font-bold uppercase tracking-overline text-white bg-red-600 hover:bg-red-700 px-1.5 py-0.5 flex items-center gap-1 disabled:opacity-50">
                            <Send size={11} strokeWidth={1.5} /> Versand fehlgeschlagen · Nachsenden
                          </button>
                        )}
                        {inv.sent_to_leistungstraeger && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`mgmt-invoice-resend-cc-${inv.invoice_id}`} disabled={resendingCc === inv.invoice_id}
                                title={inv.cc_resend_log?.length ? `Zuletzt erneut gesendet: ${new Date(inv.cc_resend_log[inv.cc_resend_log.length - 1].at).toLocaleString("de-DE")}` : undefined}
                                className="text-[10px] font-bold uppercase tracking-overline text-violet-700 hover:text-violet-900 flex items-center gap-1 disabled:opacity-50">
                                <Send size={11} strokeWidth={1.5} /> {resendingCc === inv.invoice_id ? "Sendet…" : "Erneut senden"}
                              </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent data-testid={`mgmt-invoice-resend-cc-confirm-${inv.invoice_id}`}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Rechnung {inv.invoice_number} erneut senden?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Die E-Mail, die an {inv.sent_to_leistungstraeger} ging, wird mit allen Anhängen noch einmal
                                  nur an die CC-Empfänger geschickt. Der Leistungsträger bekommt nichts doppelt.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction onClick={() => resendCc(inv.invoice_id)} data-testid={`mgmt-invoice-resend-cc-ok-${inv.invoice_id}`}>
                                  An CC senden
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button data-testid={`mgmt-invoice-delete-${inv.invoice_id}`} disabled={deletingInvoiceId === inv.invoice_id}
                              className="text-[10px] font-bold uppercase tracking-overline text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-50">
                              <Trash2 size={11} strokeWidth={1.5} /> {deletingInvoiceId === inv.invoice_id ? "…" : "Löschen"}
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`mgmt-invoice-delete-confirm-${inv.invoice_id}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Rechnung {inv.invoice_number} löschen?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Entfernt die Rechnung {inv.invoice_number} ({inv.client_name}) unwiderruflich aus der
                                Historie. Eine bereits versendete E-Mail wird dadurch NICHT zurückgezogen.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteInvoice(inv.invoice_id)} className="bg-red-600 hover:bg-red-700">
                                Ja, löschen
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        </>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent className="max-w-3xl rounded-none border border-slate-200 p-0" data-testid="invoice-preview-modal">
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
              Vorschau vor Versand
              {preview?.remaining_only && (
                <span className="px-1.5 py-0.5 bg-sky-100 text-sky-800 border border-sky-300 text-[10px] font-bold normal-case tracking-normal" data-testid="invoice-preview-partial-badge">
                  Teilrechnung · nur offener Rest
                </span>
              )}
            </div>
            <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
              Rechnung {preview?.invoice_number_preview}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 mt-2">
              Geht an <b>{preview?.kostentraeger_email}</b> ({preview?.kostentraeger}) — Betrag{" "}
              <b>{fmtEur(preview?.total_amount)}</b> ({fmtUe(preview?.total_ue)} UE). Buchhaltung erhält
              automatisch nur die Rechnung ({preview?.email_accounting_to}).
            </DialogDescription>
          </DialogHeader>
          <div className="px-8 py-4 max-h-[70vh] overflow-y-auto space-y-5">
            {preview && (
              <>
                <div className="border border-slate-200" data-testid="invoice-preview-email-block">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
                    E-Mail an den Leistungsträger
                  </div>
                  <div className="p-4 text-xs space-y-1.5">
                    <div><span className="text-slate-500">Von:</span> <span className="font-mono" data-testid="invoice-preview-email-sender">{preview.email_sender}</span></div>
                    <div><span className="text-slate-500">An:</span> <span className="font-mono" data-testid="invoice-preview-email-to">{preview.kostentraeger_email}</span></div>
                    {preview.email_cc && preview.email_cc.length > 0 && (
                      <div><span className="text-slate-500">CC:</span> <span className="font-mono" data-testid="invoice-preview-email-cc">{preview.email_cc.join(", ")}</span></div>
                    )}
                    <div><span className="text-slate-500">Antwort an:</span> <span className="font-mono" data-testid="invoice-preview-email-reply-to">{preview.email_reply_to}</span></div>
                    <div><span className="text-slate-500">Betreff:</span> <span className="font-medium" data-testid="invoice-preview-email-subject">{preview.email_subject}</span></div>
                    <div className="flex items-start gap-1.5 mt-2">
                      <Paperclip size={12} strokeWidth={1.5} className="text-slate-400 mt-0.5" />
                      <div className="flex flex-wrap gap-1.5" data-testid="invoice-preview-email-attachments">
                        {(preview.attachments || []).map((a, idx) => (
                          <button key={a.filename} onClick={() => setActiveAttachmentIdx(idx)}
                            data-testid={`invoice-preview-attachment-tab-${idx}`}
                            className={`px-2 py-0.5 border text-[10px] font-mono transition-colors ${
                              idx === activeAttachmentIdx
                                ? "bg-slate-900 border-slate-900 text-white"
                                : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                            }`}>
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 pt-1">
                      Lesebestätigung wird beim Empfänger-Mailprogramm angefragt (nicht jedes Programm unterstützt das).
                    </div>
                  </div>
                  <div
                    className="border-t border-slate-200 p-4 text-sm max-h-48 overflow-y-auto bg-slate-50"
                    data-testid="invoice-preview-email-body"
                    dangerouslySetInnerHTML={{ __html: preview.email_html }}
                  />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5" data-testid="invoice-preview-active-attachment-label">
                    Vorschau: {preview.attachments?.[activeAttachmentIdx]?.label}
                  </div>
                  <iframe
                    title={preview.attachments?.[activeAttachmentIdx]?.label || "Vorschau"}
                    data-testid="invoice-preview-pdf-frame"
                    src={`data:application/pdf;base64,${preview.attachments?.[activeAttachmentIdx]?.pdf_base64 || preview.pdf_base64}`}
                    className="w-full h-[45vh] border border-slate-200"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-8 pb-8 pt-2 flex items-center gap-3">
            <button data-testid="invoice-preview-cancel-btn" onClick={() => setPreview(null)} disabled={sending || sendingTest}
              className="px-5 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
              Abbrechen
            </button>
            <button data-testid="invoice-preview-test-email-btn" onClick={sendTestEmail} disabled={sending || sendingTest}
              className="px-5 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-2">
              <FlaskConical size={14} strokeWidth={1.5} /> {sendingTest ? "Wird gesendet…" : "Test-E-Mail an mich"}
            </button>
            <button data-testid="invoice-preview-confirm-btn" onClick={confirmSend} disabled={sending || sendingTest}
              className="bg-emerald-600 text-white px-5 py-3 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
              <Mail size={14} strokeWidth={1.5} /> {sending ? "Wird versendet…" : "Jetzt senden"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!clientPreview} onOpenChange={(open) => { if (!open) setClientPreview(null); }}>
        <DialogContent className="max-w-3xl rounded-none border border-slate-200 p-0" data-testid="invoice-client-preview-modal">
          <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Vorschau vor Versand an den Kunden</div>
            <DialogTitle className="font-heading text-3xl font-light tracking-tighter text-slate-900 mt-1">
              Rechnung {clientPreview?.invoice_number}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600 mt-2">
              Geht an <b>{clientPreview?.client_email}</b> ({clientPreview?.client_name}) — exakt dieselben
              3 Unterlagen wie an den Leistungsträger, aber mit Hinweis: keine Kosten für den Kunden, zum
              Hochladen ins Jobcenter-Portal.
            </DialogDescription>
          </DialogHeader>
          <div className="px-8 py-4 max-h-[70vh] overflow-y-auto space-y-5">
            {clientPreview && (
              <>
                <div className="border border-slate-200" data-testid="invoice-client-preview-email-block">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
                    E-Mail an den Kunden
                  </div>
                  <div className="p-4 text-xs space-y-1.5">
                    <div><span className="text-slate-500">An:</span> <span className="font-mono" data-testid="invoice-client-preview-email-to">{clientPreview.client_email}</span></div>
                    <div><span className="text-slate-500">Betreff:</span> <span className="font-medium" data-testid="invoice-client-preview-email-subject">{clientPreview.email_subject}</span></div>
                    <div className="flex items-start gap-1.5 mt-2">
                      <Paperclip size={12} strokeWidth={1.5} className="text-slate-400 mt-0.5" />
                      <div className="flex flex-wrap gap-1.5" data-testid="invoice-client-preview-email-attachments">
                        {(clientPreview.attachments || []).map((a, idx) => (
                          <button key={a.filename} onClick={() => setClientAttachmentIdx(idx)}
                            data-testid={`invoice-client-preview-attachment-tab-${idx}`}
                            className={`px-2 py-0.5 border text-[10px] font-mono transition-colors ${
                              idx === clientAttachmentIdx
                                ? "bg-slate-900 border-slate-900 text-white"
                                : "bg-slate-100 border-slate-200 text-slate-700 hover:bg-slate-200"
                            }`}>
                            {a.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div
                    className="border-t border-slate-200 p-4 text-sm max-h-48 overflow-y-auto bg-slate-50"
                    data-testid="invoice-client-preview-email-body"
                    dangerouslySetInnerHTML={{ __html: clientPreview.email_html }}
                  />
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5" data-testid="invoice-client-preview-active-attachment-label">
                    Vorschau: {clientPreview.attachments?.[clientAttachmentIdx]?.label}
                  </div>
                  <iframe
                    title={clientPreview.attachments?.[clientAttachmentIdx]?.label || "Vorschau"}
                    data-testid="invoice-client-preview-pdf-frame"
                    src={`data:application/pdf;base64,${clientPreview.attachments?.[clientAttachmentIdx]?.pdf_base64}`}
                    className="w-full h-[45vh] border border-slate-200"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter className="px-8 pb-8 pt-2 flex items-center gap-3">
            <button data-testid="invoice-client-preview-cancel-btn" onClick={() => setClientPreview(null)} disabled={sendingToClient}
              className="px-5 py-3 text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50">
              Abbrechen
            </button>
            <button data-testid="invoice-client-preview-confirm-btn" onClick={confirmSendToClient} disabled={sendingToClient}
              className="bg-sky-600 text-white px-5 py-3 text-sm font-medium hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2">
              <Send size={14} strokeWidth={1.5} /> {sendingToClient ? "Wird versendet…" : "Jetzt an Kunden senden"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CcRecipientsCard() {
  const [emails, setEmails] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/invoices/cc-recipients").then((r) => setEmails(r.data.emails)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };
  useEffect(() => { load(); }, []);

  const save = async (nextEmails) => {
    setSaving(true);
    try {
      const { data } = await api.put("/invoices/cc-recipients", { emails: nextEmails });
      setEmails(data.emails);
      toast.success("CC-Empfänger gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e || !e.includes("@")) { toast.error("Bitte eine gültige E-Mail-Adresse eingeben."); return; }
    if ((emails || []).includes(e)) { toast.error("Diese Adresse ist schon in der Liste."); return; }
    save([...(emails || []), e]);
    setNewEmail("");
  };

  const removeEmail = (e) => save((emails || []).filter((x) => x !== e));

  return (
    <div className="mb-8 border border-slate-200 bg-white" data-testid="cc-recipients-card">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-overline text-slate-500">
        CC-Empfänger für Rechnungs-Mails (Rechnung + Anwesenheitsliste + Bericht)
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-2 mb-3" data-testid="cc-recipients-list">
          {emails === null ? (
            <span className="text-xs text-slate-400 italic">Lädt…</span>
          ) : emails.length === 0 ? (
            <span className="text-xs text-slate-400 italic">Keine CC-Empfänger hinterlegt.</span>
          ) : emails.map((e) => (
            <span key={e} className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 border border-slate-200 text-xs font-mono text-slate-700" data-testid={`cc-recipient-chip-${e}`}>
              {e}
              <button onClick={() => removeEmail(e)} disabled={saving} data-testid={`cc-recipient-remove-${e}`} className="text-slate-400 hover:text-red-600 disabled:opacity-50">
                <X size={12} strokeWidth={2} />
              </button>
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input
            type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
            placeholder="neue-cc-adresse@beispiel.de" data-testid="cc-recipient-new-input"
            className="input-tech text-xs flex-1 max-w-xs"
          />
          <button onClick={addEmail} disabled={saving} data-testid="cc-recipient-add-btn"
            className="px-3 py-2 text-xs font-bold uppercase tracking-overline border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
            <Plus size={12} strokeWidth={2} /> Hinzufügen
          </button>
        </div>
      </div>
    </div>
  );
}
